import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquareText, Check, GitCompare } from "lucide-react";
import { VersionCompare } from "@/components/VersionCompare";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const STATUS = { open: "bg-amber-500/15 text-amber-300", in_progress: "bg-cyan-500/15 text-cyan-300", resolved: "bg-emerald-500/15 text-emerald-300" };
const LABEL = { open: "Open", in_progress: "In progress", resolved: "Resolved" };

const Row = ({ r, onSaved, study }) => {
  const [cmp, setCmp] = useState(false);
  const [status, setStatus] = useState(r.status);
  const [note, setNote] = useState(r.note || "");
  const save = async () => {
    try { const res = await api.put(`/revisions/${r.id}`, { status, note }); onSaved(res.data); toast.success(status === "resolved" ? "Resolved — client notified by email" : "Request updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="py-4 space-y-3" data-testid={`revision-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className={`px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${STATUS[r.status]}`}>{LABEL[r.status]}</span>
        <span>{r.client_name}</span>{r.section && <span>· {r.section}</span>}<span>· {r.created_at?.slice(0, 10)}</span>
      </div>
      <p className="text-sm text-slate-200 whitespace-pre-line">{r.message}</p>
      {r.before_version_id && (
        <button data-testid={`revision-compare-${r.id}`} onClick={() => setCmp(true)} className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:underline">
          <GitCompare className="h-3.5 w-3.5" />{r.after_version_id ? "Compare before / after" : "Compare request snapshot with current"}
        </button>
      )}
      <VersionCompare open={cmp} onOpenChange={setCmp} studyId={r.study_id} current={study} initialA={r.before_version_id} initialB={r.after_version_id || "current"} />
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid={`revision-status-${r.id}`} className="sm:col-span-3 bg-slate-950/60 border-slate-700 h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">{Object.entries(LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid={`revision-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to client (sent when resolved)" className="sm:col-span-7 bg-slate-950/60 border-slate-700 h-9" />
        <Button size="sm" data-testid={`revision-save-${r.id}`} onClick={save} className="sm:col-span-2 h-9 bg-emerald-500 text-emerald-950 hover:bg-emerald-400"><Check className="h-4 w-4 mr-1" />Save</Button>
      </div>
    </div>
  );
};

export const RevisionsPanel = ({ studyId, study }) => {
  const [revs, setRevs] = useState([]);
  useEffect(() => { api.get(`/studies/${studyId}/revisions`).then((r) => setRevs(r.data)).catch(() => {}); }, [studyId]);
  if (!revs.length) return null;
  const open = revs.filter((r) => r.status !== "resolved").length;
  return (
    <section className="no-print rounded-xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="revisions-panel">
      <h2 className="flex items-center gap-2 font-display font-semibold"><MessageSquareText className="h-5 w-5 text-amber-400" />Client change requests <span className="text-xs text-amber-300">({open} open)</span></h2>
      <div className="divide-y divide-slate-800">{revs.map((r) => <Row key={r.id} r={r} study={study} onSaved={(u) => setRevs((rs) => rs.map((x) => (x.id === u.id ? u : x)))} />)}</div>
    </section>
  );
};
